/**
 * Shape of the JSON manifest emitted by the offline anim converter
 * (`tools/anim-converter`) and consumed by the game's sprite loader. It is the
 * contract between the two: the converter bakes a UO body+action into one
 * lossless WebP atlas plus this manifest of per-frame rects and anchor offsets.
 */

/** One animation frame: its rect in the atlas and its UO per-frame anchor. */
export interface BakedFrame {
  x: number;
  y: number;
  w: number;
  h: number;
  /** `imageCenterX/Y`; honouring these per frame is what prevents walk "swim". */
  cx: number;
  cy: number;
}

export interface BakedDirection {
  /** Raw UO animation direction index 0..7 (mapped to facings by the loader). */
  direction: number;
  frames: BakedFrame[];
}

export interface BakedManifest {
  body: number;
  action: number;
  actionName: string;
  atlas: string;
  atlasWidth: number;
  atlasHeight: number;
  directions: BakedDirection[];
  /**
   * A fixed UO hue baked into this clip for the renderer to apply, for a body that is
   * always painted one colour (the energy vortex's hue 20; ADR-0148) rather than a
   * per-connection player hue. Absent for the normal player/creature bodies, which
   * carry no forced hue.
   */
  hue?: number;
}

/**
 * Manifest for one baked **effect animation** (ADR-0180): a directionless frame
 * strip decoded from static art tiles chained by `AnimData.mul` (see the `effects`
 * content registry), unlike {@link BakedManifest}'s per-direction body clips - an
 * effect looks the same from every facing, so there is no `directions` nesting,
 * just one flat frame list in play order. `frameIntervalMs` is resolved **at bake**
 * from the raw AnimData interval (`frameIntervalMs(raw)`), so the runtime never
 * re-derives client timing. Each frame's `cx`/`cy` is the frame **CENTER**
 * (`floor(w/2)`/`floor(h/2)`), not a UO anchor: static art carries no per-frame
 * anchor point, and effects draw centered on their world position (a projectile's
 * nose, an explosion's heart), so the converter overrides the anchors at bake.
 */
export interface BakedEffectManifest {
  /** The `effects` registry id (also the `effects/<id>.{webp,json}` basename). */
  id: string;
  /** The UO static art id of the effect's first frame (registry provenance). */
  artId: number;
  atlas: string;
  atlasWidth: number;
  atlasHeight: number;
  /** Milliseconds per frame, resolved at bake so runtime never re-derives it. */
  frameIntervalMs: number;
  /** The strip's frames in play order; cx/cy = frame CENTER (effects draw centered). */
  frames: BakedFrame[];
}

/** One baked land tile: its UO art id, a friendly name, and its rect in the atlas. */
export interface BakedLandTile {
  /** UO land tile id (0..0x3FFF) this rect was decoded from. */
  id: number;
  /** Friendly label the map authoring uses to refer to this tile. */
  name: string;
  x: number;
  y: number;
  w: number;
  h: number;
  /**
   * Atlas page index this rect lives on, for the multi-page **editor** bake
   * (`convert --all`, PRD #498 Slice A) whose full tile set exceeds one safe GPU
   * texture. Absent in the single-atlas game manifests, which are unchanged.
   */
  page?: number;
}

/**
 * Manifest for the baked land-tile atlas (M2). Unlike the mobile manifest there
 * are no per-frame anchors or directions - land tiles are static 44x44 diamonds.
 */
export interface BakedLandManifest {
  atlas: string;
  atlasWidth: number;
  atlasHeight: number;
  tiles: BakedLandTile[];
}

/**
 * One baked static tile: its UO art id, a friendly name, its rect in the atlas,
 * and its own width/height. Unlike land's fixed 44x44 diamonds, statics vary in
 * size, so the dimensions are recorded per tile (they equal the rect's w/h, but
 * are kept explicit as the static's intrinsic size for the renderer's anchor).
 */
export interface BakedStatic {
  /** UO static art id (the value added to 0x4000 for the archive lookup). */
  id: number;
  /** Friendly label the map authoring uses to refer to this static. */
  name: string;
  x: number;
  y: number;
  w: number;
  h: number;
  /** Intrinsic decoded width/height of the static tile. */
  width: number;
  height: number;
  /** UO TileFlag.Foliage, used as the final co-located static depth tie-break. */
  foliage?: true;
  /** Atlas page index when this entry belongs to a paged manifest. */
  page?: number;
}

export interface BakedPagedStatic extends BakedStatic {
  page: number;
}

/** Maximum dimension of one portable runtime statics texture page. */
export const RUNTIME_STATIC_ATLAS_PAGE_MAX = 2048;

/**
 * Manifest for the paged runtime statics bake.
 * Each entry carries its page plus intrinsic width and height.
 */
export interface BakedStaticsManifest {
  pages: BakedAtlasPage[];
  statics: BakedPagedStatic[];
}

/**
 * A small single-atlas static manifest used by bounded effect families such as
 * blood splats.
 */
export interface BakedSingleAtlasStaticsManifest {
  atlas: string;
  atlasWidth: number;
  atlasHeight: number;
  statics: BakedStatic[];
}

export interface BakedLightProfile {
  profile: number;
  /** Present for an exact ClassicUO color-curve variant. */
  shader?: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Exact indexed light masks decoded from the classic client's light.mul. */
export interface BakedLightManifest {
  atlas: string;
  atlasWidth: number;
  atlasHeight: number;
  profiles: BakedLightProfile[];
}

/** One atlas page: its image basename and dimensions. */
export interface BakedAtlasPage {
  atlas: string;
  width: number;
  height: number;
}

/**
 * Manifest for the full-set **editor** land bake (`convert --all`, PRD #498
 * Slice A): every decodable UO land tile, sharded across multiple atlas pages
 * (each within a safe GPU texture size); each tile's `page` indexes `pages`.
 * Editor-only - the lean single-atlas game manifests above are unchanged.
 */
export interface BakedEditorLandManifest {
  pages: BakedAtlasPage[];
  tiles: BakedLandTile[];
}

/** Statics counterpart of {@link BakedEditorLandManifest}. */
export interface BakedEditorStaticsManifest {
  pages: BakedAtlasPage[];
  statics: BakedPagedStatic[];
}

/**
 * One baked font glyph: its character code, rect in the atlas, and the layout
 * metrics (offsets + advance) a bitmap-font renderer needs. The schema mirrors a
 * BMFont char record so the runtime can hand it straight to Pixi's `BitmapFont`.
 */
export interface BakedFontGlyph {
  /** Character code this glyph renders. */
  code: number;
  x: number;
  y: number;
  w: number;
  h: number;
  /** Horizontal offset from the pen to the glyph's left edge. */
  xOffset: number;
  /** Vertical offset from the line top to the glyph's top edge. */
  yOffset: number;
  /** Pen advance to the next glyph. */
  xAdvance: number;
}

/**
 * Manifest for the baked overhead font atlas. Emitted by the offline `fonts.mul`
 * decoder and consumed by `src/render/bakedFont.ts`, which turns it into a Pixi
 * `BitmapFont` for the overhead name and speech labels. The black outline and
 * light fill are baked into the atlas (UO stores them in the glyph pixels), so
 * the runtime only tints the fill to colour speech.
 */
export interface BakedFontManifest {
  atlas: string;
  atlasWidth: number;
  atlasHeight: number;
  /** Family name the runtime registers and `BitmapText` looks up. */
  fontFamily: string;
  /** Design size in pixels; render at this size for a crisp 1:1 bitmap. */
  fontSize: number;
  /** Height of a single line of text. */
  lineHeight: number;
  /** Distance from the line top to the baseline. */
  baseLineOffset: number;
  glyphs: BakedFontGlyph[];
}
